import {
  FireTruck,
  Map,
  RequestPage,
  Store,
  TakeoutDining,
  TextSnippet,
} from "@mui/icons-material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DirectionsBoatFilledIcon from "@mui/icons-material/DirectionsBoatFilled";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { departmentHref, departmentIdFromLocation } from "../utils/procurementMode";
import { permissionsForDepartment } from "../utils/departmentAccess";
import { SIDEBAR, moduleIconSx, withModuleChildSx } from "./sidebarNavSx";

const OVERSEAS_USERS = ["Admin", "Tisura", "Avishka", "Aian", "Asiri"];

const DepartmentSidebarFolders = ({
  accent,
  departments,
  lane,
  permissions,
  departmentAccess,
  decryptedUserName,
  location,
}) => {
  const currentDeptId = departmentIdFromLocation(location);
  const [openDeptId, setOpenDeptId] = useState(null);
  const prefix = lane === "INVENTORY" ? "/inventory" : "/non-inventory";

  useEffect(() => {
    if (currentDeptId) {
      setOpenDeptId(currentDeptId);
    } else if (departments.length === 1) {
      setOpenDeptId(departments[0].id);
    }
  }, [currentDeptId, departments]);

  const links = [
    {
      perm: "view_product",
      to: `${prefix}/product-table`,
      label: "Products",
      Icon: Store,
    },
    ...(lane === "INVENTORY"
      ? [
          {
            perm: "view_product",
            to: `${prefix}/warehouse-map`,
            label: "Warehouse map",
            Icon: Map,
          },
          {
            perm: "view_min",
            to: `${prefix}/materials-issue-Note-table`,
            label: "MINs",
            Icon: TextSnippet,
          },
        ]
      : []),
    {
      perm: "view_mrn",
      to: `${prefix}/material-requisitions-table`,
      label: "MRNs",
      Icon: RequestPage,
    },
    {
      special: "overseas",
      to: `${prefix}/foreign-mrn`,
      label: "Overseas",
      Icon: DirectionsBoatFilledIcon,
    },
    {
      perm: "view_po",
      to: `${prefix}/purchase-order-table`,
      label: "POs",
      Icon: TakeoutDining,
    },
    {
      perm: "view_grn",
      to: `${prefix}/goods-received-Note-table`,
      label: "GRNs",
      Icon: FireTruck,
    },
  ];

  const canShow = (item, departmentCode) => {
    if (item.special === "overseas") {
      return OVERSEAS_USERS.includes(decryptedUserName);
    }
    const deptPerms = permissionsForDepartment(
      departmentAccess,
      departmentCode,
      permissions
    );
    return deptPerms.includes(item.perm);
  };

  const isLinkSelected = (path, departmentId) =>
    location.pathname === path && currentDeptId === departmentId;

  if (!departments.length) {
    return (
      <List component="div" disablePadding>
        {links.filter((item) => canShow(item)).map((item) => (
          <ListItemButton
            key={item.to}
            component={Link}
            to={item.to}
            sx={withModuleChildSx(accent, { pl: 3, paddingY: 1.3 })}
            selected={location.pathname === item.to && !currentDeptId}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <item.Icon sx={moduleIconSx(accent, 22)} />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontSize: 14 }}
            />
          </ListItemButton>
        ))}
      </List>
    );
  }

  return (
    <List component="div" disablePadding>
      {departments.map((dept) => {
        const isOpen = openDeptId === dept.id;
        const deptSelected =
          currentDeptId === dept.id &&
          location.pathname.startsWith(prefix) &&
          location.pathname !== `${prefix}/supplier-table` &&
          location.pathname !== `${prefix}/fuel-issue-Note-table`;

        return (
          <div
            key={dept.id}
            onMouseEnter={() => setOpenDeptId(dept.id)}
          >
            <ListItemButton
              sx={withModuleChildSx(accent, { pl: 2, paddingY: 1.1 })}
              selected={deptSelected}
              onClick={() =>
                setOpenDeptId((prev) => (prev === dept.id ? null : dept.id))
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ApartmentIcon sx={moduleIconSx(accent, 22)} />
              </ListItemIcon>
              <ListItemText
                primary={dept.name}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: "medium",
                }}
              />
              {isOpen ? (
                <ExpandLess sx={{ color: SIDEBAR.muted }} />
              ) : (
                <ExpandMore sx={{ color: SIDEBAR.muted }} />
              )}
            </ListItemButton>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {links.filter((item) => canShow(item, dept.code)).map((item) => {
                  const to = departmentHref(item.to, dept.id);
                  const selected = isLinkSelected(item.to, dept.id);
                  return (
                    <ListItemButton
                      key={`${dept.id}-${item.to}`}
                      component={Link}
                      to={to}
                      sx={withModuleChildSx(accent, { pl: 4, paddingY: 1 })}
                      selected={selected}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <item.Icon sx={moduleIconSx(accent, 20)} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: 14 }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </div>
        );
      })}
    </List>
  );
};

export default DepartmentSidebarFolders;
